import type { CSSProperties } from "react";

import { RISPOSTA } from "@/lib/promesse";

/* I tre disegni di /contatti, copiati dal modello del 19/10/2026
   (`uploads/contatti.html`) così come sono: geometria, testi e tempi.
   Coordinate letterali, nessun `Math.sin`/`Math.cos` in render (vedi l'ULP
   di ContrattiFantasy nei learnings); la sola aritmetica è quella della
   quadrettatura, interi per interi.

   - `TessereOsservatorio` è il disegno del riquadro d'apertura (`.ct-mos`):
     il segno OI fatto di 205 tessere da 7,1, collegato a TERRITORIO,
     IMPRESE e MERCATI da tre linee tratteggiate. Sostituisce `MosaicoOI`,
     il mosaico a cinque toni della versione del 01/10/2026, che non aveva
     altri utenti ed è stato cancellato con i suoi `--oi-ct-*`;
   - `MiniProfili` e `MiniMessaggio` sono le due strisce in fondo ai
     passaggi 02 e 03 del modulo (`.ct-mini`).

   I colori sono quelli del modello nei due temi (`--pi`, `--pr`, `--p0`
   = `--oi-sv-linea`, `--oi-sv-rame`, `--oi-sv-fondo`; `--mare` e
   `--segnale` per il segno), nel blocco `.oi-ct-*` di globals.css; le
   classi sono quelle del modello col prefisso `ct-`.

   Le animazioni del modello sono già CSS (`ctTes`, `oiRotta`, `ctPulsa`,
   `ctCorsa`): qui hanno gli stessi tempi, e con `prefers-reduced-motion`
   si fermano nella posa finale, cioè segno pieno, tratteggi fermi e il
   quadratino del messaggio a metà strada.

   Server Components, nessuno stato: li importa anche il modulo, che è
   client, e lì diventano markup del suo albero. */

/** La quadrettatura (`g.ct-gr` / `g.gr` del modello): verticali ogni 40
 *  da 40, orizzontali al passo dato. */
function Griglia({ larga, alta, passo }: { larga: number; alta: number; passo: number }) {
  const xs = Array.from({ length: Math.floor((larga - 1) / 40) }, (_, i) => (i + 1) * 40);
  const ys = Array.from({ length: Math.floor((alta - 1) / passo) }, (_, i) => (i + 1) * passo);
  return (
    <g className="ct-gr">
      {xs.map((x) => (
        <line key={`x${x}`} x1={x} y1="0" x2={x} y2={alta} />
      ))}
      {ys.map((y) => (
        <line key={`y${y}`} x1="0" y1={y} x2={larga} y2={y} />
      ))}
    </g>
  );
}

/** Le tessere del segno: classe (`to` mare, `tr` rame del triangolo), x,
 *  y e il ritardo dell'affievolirsi, in secondi. Righe di quattro, in
 *  ordine di documento come nel modello. */
const TESSERE: ["to" | "tr", number, number, number][] = [
  ["to", 218.7, 22.0, 0.4], ["to", 228.0, 22.0, 0.6], ["to", 237.4, 22.0, 0.8], ["to", 246.7, 22.0, 1.0],
  ["to", 256.1, 22.0, 1.2], ["to", 265.4, 22.0, 1.4], ["to", 274.8, 22.0, 1.6], ["to", 284.1, 22.0, 1.8],
  ["to", 293.4, 22.0, 2.0], ["to", 302.8, 22.0, 2.2], ["to", 312.1, 22.0, 2.4], ["tr", 330.8, 22.0, 0.0],
  ["tr", 340.2, 22.0, 0.2], ["tr", 349.5, 22.0, 0.4], ["tr", 358.9, 22.0, 0.6], ["tr", 368.2, 22.0, 0.8],
  ["tr", 377.6, 22.0, 1.0], ["to", 209.3, 31.3, 0.4], ["to", 218.7, 31.3, 0.6], ["to", 228.0, 31.3, 0.8],
  ["to", 237.4, 31.3, 1.0], ["to", 246.7, 31.3, 1.2], ["to", 256.1, 31.3, 1.4], ["to", 265.4, 31.3, 1.6],
  ["to", 274.8, 31.3, 1.8], ["to", 284.1, 31.3, 2.0], ["to", 293.4, 31.3, 2.2], ["to", 302.8, 31.3, 2.4],
  ["to", 312.1, 31.3, 2.6], ["to", 321.5, 31.3, 0.0], ["tr", 340.2, 31.3, 0.4], ["tr", 349.5, 31.3, 0.6],
  ["tr", 358.9, 31.3, 0.8], ["tr", 368.2, 31.3, 1.0], ["tr", 377.6, 31.3, 1.2], ["to", 200.0, 40.7, 0.4],
  ["to", 209.3, 40.7, 0.6], ["to", 218.7, 40.7, 0.8], ["to", 228.0, 40.7, 1.0], ["to", 237.4, 40.7, 1.2],
  ["to", 246.7, 40.7, 1.4], ["to", 256.1, 40.7, 1.6], ["to", 265.4, 40.7, 1.8], ["to", 274.8, 40.7, 2.0],
  ["to", 284.1, 40.7, 2.2], ["to", 293.4, 40.7, 2.4], ["to", 302.8, 40.7, 2.6], ["to", 312.1, 40.7, 0.0],
  ["to", 321.5, 40.7, 0.2], ["to", 330.8, 40.7, 0.4], ["tr", 349.5, 40.7, 0.8], ["tr", 358.9, 40.7, 1.0],
  ["tr", 368.2, 40.7, 1.2], ["tr", 377.6, 40.7, 1.4], ["to", 200.0, 50.0, 0.6], ["to", 209.3, 50.0, 0.8],
  ["to", 218.7, 50.0, 1.0], ["to", 228.0, 50.0, 1.2], ["to", 302.8, 50.0, 0.0], ["to", 312.1, 50.0, 0.2],
  ["to", 321.5, 50.0, 0.4], ["to", 330.8, 50.0, 0.6], ["tr", 358.9, 50.0, 1.2], ["tr", 368.2, 50.0, 1.4],
  ["tr", 377.6, 50.0, 1.6], ["to", 200.0, 59.4, 0.8], ["to", 209.3, 59.4, 1.0], ["to", 218.7, 59.4, 1.2],
  ["to", 312.1, 59.4, 0.4], ["to", 321.5, 59.4, 0.6], ["to", 330.8, 59.4, 0.8], ["to", 349.5, 59.4, 1.2],
  ["tr", 368.2, 59.4, 1.6], ["tr", 377.6, 59.4, 1.8], ["to", 200.0, 68.7, 1.0], ["to", 209.3, 68.7, 1.2],
  ["to", 218.7, 68.7, 1.4], ["to", 312.1, 68.7, 0.6], ["to", 321.5, 68.7, 0.8], ["to", 330.8, 68.7, 1.0],
  ["to", 349.5, 68.7, 1.4], ["to", 358.9, 68.7, 1.6], ["tr", 377.6, 68.7, 2.0], ["to", 200.0, 78.1, 1.2],
  ["to", 209.3, 78.1, 1.4], ["to", 218.7, 78.1, 1.6], ["to", 312.1, 78.1, 0.8], ["to", 321.5, 78.1, 1.0],
  ["to", 330.8, 78.1, 1.2], ["to", 349.5, 78.1, 1.6], ["to", 358.9, 78.1, 1.8], ["to", 368.2, 78.1, 2.0],
  ["to", 200.0, 87.4, 1.4], ["to", 209.3, 87.4, 1.6], ["to", 218.7, 87.4, 1.8], ["to", 312.1, 87.4, 1.0],
  ["to", 321.5, 87.4, 1.2], ["to", 330.8, 87.4, 1.4], ["to", 349.5, 87.4, 1.8], ["to", 358.9, 87.4, 2.0],
  ["to", 368.2, 87.4, 2.2], ["to", 377.6, 87.4, 2.4], ["to", 200.0, 96.8, 1.6], ["to", 209.3, 96.8, 1.8],
  ["to", 218.7, 96.8, 2.0], ["to", 312.1, 96.8, 1.2], ["to", 321.5, 96.8, 1.4], ["to", 330.8, 96.8, 1.6],
  ["to", 349.5, 96.8, 2.0], ["to", 358.9, 96.8, 2.2], ["to", 368.2, 96.8, 2.4], ["to", 377.6, 96.8, 2.6],
  ["to", 200.0, 106.1, 1.8], ["to", 209.3, 106.1, 2.0], ["to", 218.7, 106.1, 2.2], ["to", 312.1, 106.1, 1.4],
  ["to", 321.5, 106.1, 1.6], ["to", 330.8, 106.1, 1.8], ["to", 349.5, 106.1, 2.2], ["to", 358.9, 106.1, 2.4],
  ["to", 368.2, 106.1, 2.6], ["to", 377.6, 106.1, 0.0], ["to", 200.0, 115.5, 2.0], ["to", 209.3, 115.5, 2.2],
  ["to", 218.7, 115.5, 2.4], ["to", 312.1, 115.5, 1.6], ["to", 321.5, 115.5, 1.8], ["to", 330.8, 115.5, 2.0],
  ["to", 349.5, 115.5, 2.4], ["to", 358.9, 115.5, 2.6], ["to", 368.2, 115.5, 0.0], ["to", 377.6, 115.5, 0.2],
  ["to", 200.0, 124.8, 2.2], ["to", 209.3, 124.8, 2.4], ["to", 218.7, 124.8, 2.6], ["to", 312.1, 124.8, 1.8],
  ["to", 321.5, 124.8, 2.0], ["to", 330.8, 124.8, 2.2], ["to", 349.5, 124.8, 2.6], ["to", 358.9, 124.8, 0.0],
  ["to", 368.2, 124.8, 0.2], ["to", 377.6, 124.8, 0.4], ["to", 200.0, 134.1, 2.4], ["to", 209.3, 134.1, 2.6],
  ["to", 218.7, 134.1, 0.0], ["to", 228.0, 134.1, 0.2], ["to", 302.8, 134.1, 1.8], ["to", 312.1, 134.1, 2.0],
  ["to", 321.5, 134.1, 2.2], ["to", 330.8, 134.1, 2.4], ["to", 349.5, 134.1, 0.0], ["to", 358.9, 134.1, 0.2],
  ["to", 368.2, 134.1, 0.4], ["to", 377.6, 134.1, 0.6], ["to", 200.0, 143.5, 2.6], ["to", 209.3, 143.5, 0.0],
  ["to", 218.7, 143.5, 0.2], ["to", 228.0, 143.5, 0.4], ["to", 237.4, 143.5, 0.6], ["to", 246.7, 143.5, 0.8],
  ["to", 256.1, 143.5, 1.0], ["to", 265.4, 143.5, 1.2], ["to", 274.8, 143.5, 1.4], ["to", 284.1, 143.5, 1.6],
  ["to", 293.4, 143.5, 1.8], ["to", 302.8, 143.5, 2.0], ["to", 312.1, 143.5, 2.2], ["to", 321.5, 143.5, 2.4],
  ["to", 330.8, 143.5, 2.6], ["to", 349.5, 143.5, 0.2], ["to", 358.9, 143.5, 0.4], ["to", 368.2, 143.5, 0.6],
  ["to", 377.6, 143.5, 0.8], ["to", 209.3, 152.8, 0.2], ["to", 218.7, 152.8, 0.4], ["to", 228.0, 152.8, 0.6],
  ["to", 237.4, 152.8, 0.8], ["to", 246.7, 152.8, 1.0], ["to", 256.1, 152.8, 1.2], ["to", 265.4, 152.8, 1.4],
  ["to", 274.8, 152.8, 1.6], ["to", 284.1, 152.8, 1.8], ["to", 293.4, 152.8, 2.0], ["to", 302.8, 152.8, 2.2],
  ["to", 312.1, 152.8, 2.4], ["to", 321.5, 152.8, 2.6], ["to", 349.5, 152.8, 0.4], ["to", 358.9, 152.8, 0.6],
  ["to", 368.2, 152.8, 0.8], ["to", 377.6, 152.8, 1.0], ["to", 218.7, 162.2, 0.6], ["to", 228.0, 162.2, 0.8],
  ["to", 237.4, 162.2, 1.0], ["to", 246.7, 162.2, 1.2], ["to", 256.1, 162.2, 1.4], ["to", 265.4, 162.2, 1.6],
  ["to", 274.8, 162.2, 1.8], ["to", 284.1, 162.2, 2.0], ["to", 293.4, 162.2, 2.2], ["to", 302.8, 162.2, 2.4],
  ["to", 312.1, 162.2, 2.6], ["to", 349.5, 162.2, 0.6], ["to", 358.9, 162.2, 0.8], ["to", 368.2, 162.2, 1.0],
  ["to", 377.6, 162.2, 1.2],
];

/** Il segno OI a tessere fra territorio, imprese e mercati. */
export function TessereOsservatorio() {
  return (
    <svg
      className="oi-ct-disegno ct-mos"
      viewBox="0 0 560 190"
      role="img"
      aria-label="Il segno dell’Osservatorio, composto di tessere, tra territorio, imprese e mercati"
    >
      <Griglia larga={560} alta={190} passo={38} />
      <path className="ct-fl" d="M70 60 C130 60 150 90 196 92" />
      <path className="ct-fl" d="M70 150 C130 150 150 110 196 104" />
      <path className="ct-fl" d="M392 96 C430 96 450 70 492 70" />
      <rect className="ct-pt" x="62" y="52" width="10" height="10" />
      <rect className="ct-pt" x="62" y="142" width="10" height="10" />
      <rect className="ct-pt ct-r" x="488" y="65" width="10" height="10" />
      <text x="30" y="44">TERRITORIO</text>
      <text x="40" y="172">IMPRESE</text>
      <text x="468" y="54">MERCATI</text>
      <text className="ct-cd" x="396" y="176">
        OGNI RICHIESTA · UNA DOMANDA
      </text>
      {TESSERE.map(([c, x, y, r]) => (
        <rect
          key={`${x}-${y}`}
          className={`ct-${c}`}
          x={x}
          y={y}
          width="7.1"
          height="7.1"
          style={{ animationDelay: `${r}s` }}
        />
      ))}
    </svg>
  );
}

/** Il segno OI piccolo in fondo alle due strisce (`translate(500 35)
 *  scale(0.075)` del modello), mare col triangolo rame. */
function SegnoPiccolo() {
  return (
    <g transform="translate(500 35) scale(0.075)">
      <path
        className="ct-o"
        fillRule="evenodd"
        d="M61 0H276L337 61V294L276 355H61L0 294V61ZM77 93V262L97 282H240L260 262V93L240 73H97Z"
      />
      <path className="ct-tri" d="M303 0H445V136Z" />
      <path className="ct-o" d="M357 75L445 159V355H357Z" />
    </g>
  );
}

/** La striscia del passaggio 02: tre profili che convergono su «TU», e
 *  da lì la linea all'Osservatorio. */
export function MiniProfili() {
  return (
    <svg className="oi-ct-disegno ct-mini" viewBox="0 0 600 96" aria-hidden>
      <Griglia larga={600} alta={96} passo={32} />
      <path className="ct-fl" d="M84 22 C170 22 200 48 268 48" />
      <path className="ct-fl" d="M84 48 H268" />
      <path className="ct-fl" d="M84 74 C170 74 200 48 268 48" />
      <rect className="ct-sq" x="74" y="17" width="10" height="10" />
      <rect className="ct-sq" x="74" y="43" width="10" height="10" />
      <rect className="ct-sq" x="74" y="69" width="10" height="10" />
      <text x="30" y="26">PMI</text>
      <text x="30" y="52">ENTE</text>
      <text x="30" y="78">STUDIO</text>
      <circle className="ct-tu" cx="276" cy="48" r="8" />
      <circle className="ct-pl" cx="276" cy="48" r="8" />
      <text x="266" y="80">TU</text>
      <path className="ct-rt" d="M286 48 H492" />
      <SegnoPiccolo />
      <text className="ct-cd" x="330" y="40">
        UNA PERSONA TI RISPONDE
      </text>
    </svg>
  );
}

/** La linea su cui corre il quadratino del messaggio: è anche la linea
 *  tratteggiata disegnata sotto, e la stessa stringa dell'`offset-path`
 *  del modello. */
const CORSA_MESSAGGIO = "M108 48 C200 10 380 86 492 48";

/** Il quadratino che corre: la posa da fermo è a metà della linea. */
const QUADRATINO: CSSProperties = {
  offsetPath: `path("${CORSA_MESSAGGIO}")`,
  offsetDistance: "50%",
};

/** La striscia del passaggio 03: il foglio del messaggio, la linea che
 *  porta all'Osservatorio, «ENTRO 48H» in rame (da `RISPOSTA`, come ogni
 *  altra promessa del sito). */
export function MiniMessaggio() {
  return (
    <svg className="oi-ct-disegno ct-mini" viewBox="0 0 600 96" aria-hidden>
      <Griglia larga={600} alta={96} passo={32} />
      <rect className="ct-doc" x="60" y="22" width="40" height="52" />
      <line className="ct-tx" x1="68" y1="34" x2="92" y2="34" />
      <line className="ct-tx" x1="68" y1="44" x2="86" y2="44" />
      <line className="ct-tx" x1="68" y1="54" x2="92" y2="54" />
      <line className="ct-tx" x1="68" y1="64" x2="84" y2="64" />
      <path className="ct-rt" d={CORSA_MESSAGGIO} />
      <rect className="ct-mv" x="-5" y="-5" width="10" height="10" style={QUADRATINO} />
      <SegnoPiccolo />
      <text x="44" y="88">IL TUO MESSAGGIO</text>
      <text className="ct-r" x="498" y="88">
        ENTRO {RISPOSTA.breve.toUpperCase()}
      </text>
    </svg>
  );
}
